import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useTranslation } from 'react-i18next';
import { RequestedAppUpdateInfosManagement } from '../components/RequestedAppUpdateInfosManagement';
import {
  requestedAppUpdateSchema,
  TRequestedAppUpdateSchema,
} from '@/schemas/requested-app-update/requested-app-update.schema';
import {
  useUpsertRequestedAppUpdate,
  useGetFirstRequestedAppUpdate,
} from '@/hooks/requested-app-update.hooks';
import { PhoneIcon } from '@/components/SvgIcons/PhoneIcon';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { showModal } from '@/utils/modal.utils';
import Loader from '@/components/Loading/Loader';

export const RequestAppUpdate = () => {
  const { t } = useTranslation('requestedAppUpdate');
  const [confirmAppUpdate, setConfirmAppUpdate] = useState(false);
  const [submittedRequestedAppUpdate, setSubmittedRequestedAppUpdate] =
    useState<TRequestedAppUpdateSchema>();
  const { data, isLoading } = useGetFirstRequestedAppUpdate();

  const {
    control,
    register,
    setValue,
    watch,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TRequestedAppUpdateSchema>({
    resolver: zodResolver(requestedAppUpdateSchema),
  });

  const { mutate, isPending } = useUpsertRequestedAppUpdate({
    t,
    handleSuccess: () => {
      setConfirmAppUpdate(false);
      setSubmittedRequestedAppUpdate(undefined);
    },
    handleError: () => {
      setConfirmAppUpdate(false);
      setSubmittedRequestedAppUpdate(undefined);
    },
  });

  useEffect(() => {
    if (confirmAppUpdate && submittedRequestedAppUpdate) {
      mutate(submittedRequestedAppUpdate);
    }
  }, [confirmAppUpdate, mutate, submittedRequestedAppUpdate]);

  const onSubmitRequestedAppUpdate = (
    requestedAppUpdate?: TRequestedAppUpdateSchema
  ) => {
    const requestedAppUpdateWithId = {
      id: data?.data?.id,
      ...requestedAppUpdate,
    };

    setSubmittedRequestedAppUpdate(requestedAppUpdateWithId);

    showModal('#app-update-confirmation-modal');
  };

  return (
    <>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-[26px] h-[26px] [&>path:first-child]:fill-primary [&>path:nth-child(2)]:fill-success" />
            <div>{t('common:requestedAppUpdate')}</div>
          </div>
        }
      />
      <div className="flex flex-col gap-12">
        <RequestedAppUpdateInfosManagement<TRequestedAppUpdateSchema>
          t={t}
          data={!isLoading && data?.data}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isSubmitting || isPending || isLoading}
          onSubmit={handleSubmit(onSubmitRequestedAppUpdate)}
        />
      </div>
      <ConfirmationModal
        id="app-update-confirmation-modal"
        title={t('common:confirmation')}
        description={`${t('common:areYouSureToConfirmTheseChanges')} ?`}
        onCancelLabel={t('common:no')}
        onConfirmLabel={t('common:yes')}
        onConfirm={() => setConfirmAppUpdate(true)}
        onCancel={() => setConfirmAppUpdate(false)}
      />
      {isLoading && <Loader />}
    </>
  );
};
